import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const TermsOfServicePage = () => {
  const lastUpdated = 'February 12, 2026';
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="terms-back-home">
            RealProp
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-black transition-colors" data-testid="terms-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-screen-md mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="terms-title">
          Terms of Service
        </h1>
        <p className="text-sm text-gray-500 mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>Last updated: {lastUpdated}</p>

        <div className="prose prose-gray max-w-none space-y-8" style={{ fontFamily: 'Manrope, sans-serif' }}>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of the RealProp website at <a href="https://realprop.io" className="text-violet-600 underline">https://realprop.io</a> and the related services (the "Service"), operated by RealProp ("we", "us", "our").
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              By creating an account or using the Service, you agree to these Terms and our <Link to="/privacy" className="text-violet-600 underline">Privacy Policy</Link>. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Eligibility</h2>
            <p className="text-gray-700 leading-relaxed">
              You must be at least 18 years old and have the legal authority to enter into these Terms. The Service is intended for licensed real estate professionals and their teams.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Account & Security</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You agree to provide accurate, current, and complete information.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>Notify us immediately at <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a> of any unauthorized access.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Subscriptions & Billing</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>The Service offers Free, Starter, Pro, and Team plans with different feature limits.</li>
              <li>Paid plans are billed monthly or annually via PayPal. Subscriptions renew automatically unless cancelled.</li>
              <li>You authorize us (via PayPal) to charge the payment method on file at the start of each billing cycle.</li>
              <li>All fees are non-refundable except where required by law or as outlined in our refund policy below.</li>
              <li>We may change pricing at any time. Pricing changes apply to subsequent billing cycles and we will notify you in advance.</li>
            </ul>
            <h3 className="font-semibold mt-4 mb-1">Refunds</h3>
            <p className="text-gray-700 leading-relaxed">
              Subscription fees are non-refundable. We may, at our sole discretion, issue a prorated refund for the unused portion of a paid billing cycle if you cancel within 7 days of the initial charge and have not used core paid features (HD video reels, Magic Scrapes beyond the free tier, etc.).
            </p>
            <h3 className="font-semibold mt-4 mb-1">Cancellation</h3>
            <p className="text-gray-700 leading-relaxed">
              You may cancel your subscription anytime from your Account Settings or by contacting support. Cancellation takes effect at the end of the current billing period — you retain access to paid features until then.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Acceptable Use</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You agree NOT to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>Use the Service for any illegal, fraudulent, or unauthorized purpose.</li>
              <li>Publish marketing content for properties you do not have authority to represent.</li>
              <li>Scrape or import data from sources you do not have the right to use, or in violation of those sources' terms.</li>
              <li>Attempt to circumvent plan limits, access controls, or security features.</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Service.</li>
              <li>Resell, sublicense, or commercially exploit the Service without our written permission.</li>
              <li>Upload viruses, malicious code, or content that infringes others' rights.</li>
              <li>Send spam or unsolicited communications via the email tools.</li>
              <li>Interfere with or disrupt the Service or networks connected to it.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Your Content & License</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              "Your Content" means everything you upload, paste, or generate via the Service — including listing photos, descriptions, scraped property data, brand kits, and any content created using our AI tools.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-1">
              <li>You retain ownership of Your Content.</li>
              <li>You grant us a worldwide, royalty-free, non-exclusive license to host, store, process, transmit, and display Your Content for the purpose of operating the Service.</li>
              <li>You represent that you have all rights necessary to upload Your Content (including images, addresses, and any third-party content), and that Your Content does not infringe any third-party rights.</li>
              <li>You are responsible for ensuring that your use of scraped listing data complies with applicable laws and the source platforms' terms of service.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. AI-Generated Content</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service uses third-party AI models (OpenAI, Anthropic, Google, and others) to generate captions, descriptions, images, and video reels. AI output may contain factual errors, fabricated details, or biased content. <strong>You are solely responsible for reviewing AI-generated content for accuracy, compliance with MLS / NAR / Fair Housing rules, and suitability before publishing it.</strong>
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              We do not guarantee that AI output is free of errors, original, or fit for any particular purpose. Generated content is provided "as is" and you assume all risk associated with its use.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Third-Party Integrations</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service integrates with third-party platforms (Meta/Instagram, Google, PayPal, Apify, Supabase, Resend, and others). When you connect or use those integrations, you also agree to those providers' own terms and privacy policies. We are not responsible for the availability, content, or actions of third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service, including its templates, code, designs, logos, and trademarks, is owned by RealProp and protected by copyright and other intellectual-property laws. Nothing in these Terms grants you any right to our trademarks or branding.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Disclaimers</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service is provided <strong>"AS IS" and "AS AVAILABLE"</strong> without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, or uninterrupted availability. We do not warrant that the Service will be error-free, secure, or meet your specific requirements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, RealProp and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill, arising out of or related to your use of the Service. Our total aggregate liability for any claim shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b) USD $100.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>12. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless RealProp from any claim, damage, or expense (including reasonable attorneys' fees) arising out of (a) your use of the Service, (b) your violation of these Terms, (c) Your Content, or (d) your violation of any third-party rights or applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>13. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate your account at any time for breach of these Terms, suspected fraud, or for any other reason at our discretion. You may close your account at any time from Account Settings. Upon termination, your right to use the Service ceases immediately and we may delete your data per our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>14. Changes to the Service & Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify, suspend, or discontinue any part of the Service at any time. We may update these Terms; material changes will be notified by email or in-app notice. Continued use of the Service after changes take effect constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>15. Governing Law & Disputes</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of laws principles. Any dispute shall be resolved exclusively in the state or federal courts located in Delaware, and you consent to personal jurisdiction in those courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>16. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              Questions about these Terms? Email <a href="mailto:support@realprop.io" className="text-violet-600 underline">support@realprop.io</a>.
            </p>
          </section>

        </div>

        <div className="mt-16 pt-8 border-t border-gray-200 text-center text-sm text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/privacy" className="text-violet-600 underline mr-4" data-testid="link-to-privacy">Privacy Policy</Link>
          <Link to="/data-deletion" className="text-violet-600 underline mr-4" data-testid="link-to-data-deletion">Data Deletion</Link>
          <Link to="/" className="text-violet-600 underline">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
